import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete, Input, Spin, Typography } from "antd";
import { ensureGoogleMapsPlacesLoaded } from "utils/googleMapsLoader";

const GooglePlacesAutocompleteInput = ({
  googleMapsApiKey,
  placeholder,
  types,
  disabled,
  onResolvePlace,
  value,
  onChange,
  allowClear = true,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const placesContainerRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const inputNameRef = useRef(
    `places-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`,
  );

  const [options, setOptions] = useState([]);
  const [internalQuery, setInternalQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const query = value !== undefined ? value : internalQuery;
  const setQuery = (next) => {
    const v = next ?? "";
    if (onChange) onChange(v);
    if (value === undefined) setInternalQuery(v);
  };

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    setIsLoaded(false);
    ensureGoogleMapsPlacesLoaded(googleMapsApiKey)
      .then((ok) => {
        if (!alive) return;
        setIsLoaded(Boolean(ok));
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err);
        setIsLoaded(false);
      });
    return () => {
      alive = false;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (!isLoaded || !googleMapsApiKey) return;
    if (!window.google?.maps?.places) return;

    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }

    if (!placesContainerRef.current) {
      placesContainerRef.current = document.createElement("div");
    }

    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        placesContainerRef.current,
      );
    }
  }, [isLoaded, googleMapsApiKey]);

  const effectiveDisabled = disabled || !googleMapsApiKey || Boolean(loadError) || !isLoaded;

  const help = useMemo(() => {
    if (loadError) return "Google Maps failed to load.";
    if (!googleMapsApiKey) return "Google Maps API key not available.";
    if (!isLoaded) return "Loading Google Maps...";
    return null;
  }, [googleMapsApiKey, isLoaded, loadError]);

  const search = useCallback(
    (text) => {
      setQuery(text);
      const value = String(text || "").trim();
      const svc = autocompleteServiceRef.current;
      if (effectiveDisabled || !svc || value.length < 2) {
        setOptions([]);
        return;
      }

      const seq = ++fetchSeqRef.current;
      setLoading(true);
      svc.getPlacePredictions(
        { input: value, types: Array.isArray(types) ? types : undefined },
        (predictions, status) => {
          if (seq !== fetchSeqRef.current) return;
          const safe = Array.isArray(predictions) ? predictions : [];
          const seenPlaceIds = new Set();
          const seenDescriptions = new Set();
          const next = safe
            .map((p) => ({
              value: p.description,
              label: p.description,
              placeId: p.place_id,
            }))
            .filter((item) => {
              const descKey = String(item.value || "").trim().toLowerCase();
              if (!descKey) return false;
              // Google can return duplicate descriptions with different place IDs.
              if (seenDescriptions.has(descKey)) return false;
              if (item.placeId && seenPlaceIds.has(item.placeId)) return false;
              seenDescriptions.add(descKey);
              if (item.placeId) seenPlaceIds.add(item.placeId);
              return true;
            });
          setOptions(next);
          setLoading(false);
        },
      );
    },
    [effectiveDisabled, types],
  );

  const resolveDetails = useCallback(
    (placeId) =>
      new Promise((resolve, reject) => {
        const svc = placesServiceRef.current;
        if (!svc || !placeId) return reject(new Error("PlacesService not ready"));
        svc.getDetails(
          { placeId, fields: ["address_components", "formatted_address", "place_id"] },
          (result, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !result) {
              reject(new Error(`Place details failed: ${status || "unknown"}`));
              return;
            }
            resolve(result);
          },
        );
      }),
    [],
  );

  const onSelect = useCallback(
    async (value, option) => {
      const placeId = option?.placeId;
      if (!placeId || !onResolvePlace) return;
      setSelecting(true);
      try {
        // Selecting an option can trigger both `onSelect` and `onChange` in AutoComplete.
        // Force the displayed query to the chosen value and close suggestions immediately.
        setQuery(String(value || ""));
        setOptions([]);
        const details = await resolveDetails(placeId);
        await onResolvePlace({ details, prediction: { description: value, placeId } });
      } finally {
        setSelecting(false);
      }
    },
    [onResolvePlace, resolveDetails],
  );

  return (
    <div>
      <AutoComplete
        value={query}
        options={options}
        onSearch={search}
        onSelect={onSelect}
        onChange={(val) => setQuery(val)}
        disabled={effectiveDisabled}
        allowClear={allowClear}
        placeholder={placeholder}
        // Show loading state without custom child Input to avoid duplicated "selected item + input"
        // rendering inside the same row.
        suffixIcon={selecting || loading ? <Spin size="small" /> : null}
        notFoundContent={loading ? <Spin size="small" /> : null}
        style={{ width: "100%" }}
        // Guard against Chrome/Google autofill injecting values into the input.
        // A stable-but-unique name + "new-password" is more effective than "off" in many browsers.
        name={inputNameRef.current}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        inputMode="search"
      />
      {help && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {help}
        </Typography.Text>
      )}
    </div>
  );
};

export default GooglePlacesAutocompleteInput;
